// ui/src/components/finance/FinanceCompDetail.jsx
/**
 * @file FinanceCompDetail.jsx
 * @module ui/src/components/finance/FinanceCompDetail
 * @description
 *   Provides Finance drill-down from plan-period aggregates to position-level persisted
 *   payout evidence. The component renders server-recorded payout_summary and payout_detail
 *   artifacts without recalculating, reconstructing, or inferring compensation economics.
 *
 * @version 1.0.0
 * @since S756
 * @author data-fit LLC · DecisionFlow · S776
 *
 * @architecture
 *   Locked decisions : #1, #25
 *   Temporal invariants : INV-1, INV-3, INV-7, INV-9
 *   Design principles : DP-1, DP-5, DP-6, DP-12, DP-14
 *   Cross-cutting contracts : XC-1, XC-5, XC-6, XC-7
 *
 * @domain-rules
 *   - Every payout_detail row supplied by the engine is rendered; the UI never maintains
 *     a fixed list of payable stages.
 *   - Resolved payout, rate, input, FX, gate, and summary values are displayed exactly
 *     from persisted engine evidence and are never recalculated.
 *   - Plan effective dates are visible as economic context and are independent of status.
 *   - Split-period attribution is delegated to VersionAttributionPanel.
 *   - Economic/runtime evidence is not presented as plan-document validity.
 *
 * @session-6-migration-flags
 *   - plan.effective_from / plan.effective_to — pending m_tc_01
 *   - calculation_run.resolution_cutoff_at — pending m_tc_02
 *   - calculation_run.engine_release_id / engine_build_hash — pending m_tc_03
 *   - GET /api/v1/finance/plan/{id}/detail — Session 6 API gap
 *   - GET /api/v1/finance/position/{id}/payout — Session 6 API gap
 *
 * @changelog
 *   1.0.0  S756  Initial build — Group Finance batch delivery
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import VersionAttributionPanel from './VersionAttributionPanel';

/**
 * @typedef {Object} PlanPositionRow
 * @property {string} positionId
 * @property {string} positionName
 * @property {string} employeeId
 * @property {number|null} quotaAmount
 * @property {number|null} attainmentPct
 * @property {number|null} calculatedPayout
 * @property {'available'|'not_available'|'partial'} payoutState
 * @property {string|null} calculationRunId
 */

/**
 * @typedef {Object} FinanceCompDetailProps
 * @property {number} planId
 * @property {number} fiscalPeriodId
 * @property {string|null} positionId
 */

const FINANCIAL_STATES = ['available', 'not_available', 'partial'];

const STAGE_LABELS = {
  arr: 'Quota attainment · base payout',
  mbo: 'MBO payout',
  nps: 'NPS payout',
  adoption: 'Adoption payout',
  draw_advance: 'Draw advance',
  draw_recovery: 'Draw recovery',
  draw_guarantee: 'Draw guarantee',
  clawback: 'Clawback recovery',
  spiff: 'SPIFF / tactical incentive',
};

const styles = {
  breadcrumbs: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  meta: {
    display: 'grid',
    gap: '0.75rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))',
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem',
  },
  label: {
    color: 'rgb(var(--color-text-muted))',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  metricGrid: {
    display: 'grid',
    gap: '0.75rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))',
  },
  metric: {
    background: 'rgb(var(--color-surface-muted))',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '0.5rem',
    padding: '0.75rem',
  },
  numeric: {
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
  },
  evidenceGrid: {
    display: 'grid',
    gap: '0.5rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))',
  },
  evidenceRow: {
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '0.5rem',
    padding: '0.75rem',
  },
  raw: {
    background: 'rgb(var(--color-surface-muted))',
    margin: 0,
    overflowX: 'auto',
    padding: '0.5rem',
    whiteSpace: 'pre-wrap',
  },
};

function formatNumber(value, maximumFractionDigits = 2) {
  // WHY: Null and invalid engine fields remain explicitly unavailable and are never
  // coerced to zero because zero is a distinct valid result under XC-6.
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 'Not available';
  }

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits,
    minimumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value) {
  // WHY: Attainment is persisted evidence; this function only formats the recorded
  // value and performs no attainment computation.
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 'Not available';
  }

  return `${new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(value)}%`;
}

function formatMoney(value, currency = 'USD') {
  // WHY: Numeric zero must remain visible as a valid engine result.
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 'Not available';
  }

  try {
    return new Intl.NumberFormat(undefined, {
      currency,
      style: 'currency',
    }).format(value);
  } catch {
    return formatNumber(value, 2);
  }
}

function ThreeStateValue({
  value,
  state,
  formatter,
}) {
  // WHY: Constrained results are economically distinct from available values.
  if (state === 'partial') {
    return (
      <span className="modifier-manager-badge modifier-manager-badge--incomplete">
        Incomplete / partial
      </span>
    );
  }

  // WHY: Absence of a result must never render as a blank financial cell.
  if (state === 'not_available') {
    return <span>Not available</span>;
  }

  // WHY: Available plus null is invalid evidence and must not silently become zero.
  if (value === null || value === undefined) {
    return <span>Not available</span>;
  }

  return <span>{formatter(value)}</span>;
}

ThreeStateValue.propTypes = {
  formatter: PropTypes.func.isRequired,
  state: PropTypes.oneOf(FINANCIAL_STATES).isRequired,
  value: PropTypes.number,
};

ThreeStateValue.defaultProps = {
  value: null,
};

function EvidenceRows({ rows }) {
  // WHY: No recorded detail means no engine-stage evidence exists; the UI must not
  // invent expected component stages.
  if (!rows.length) {
    return (
      <div className="modifier-manager-empty-state" role="status">
        No payout-detail rows were recorded for this position.
      </div>
    );
  }

  return (
    <div>
      {rows.map((row, index) => {
        const stageLabel =
          STAGE_LABELS[row.component_type]
          || STAGE_LABELS[row.componentType]
          || row.component_type
          || row.componentType
          || 'Recorded payout detail';

        const inputValue = row.input_value ?? row.inputValue;
        const resolvedRate = row.resolved_rate ?? row.resolvedRate;
        const resolvedPayout = row.resolved_payout ?? row.resolvedPayout;
        const currency =
          row.payout_currency_code
          ?? row.payoutCurrencyCode
          ?? 'USD';
        const fxRate = row.fx_rate_used ?? row.fxRateUsed;
        const payload = row.detail_payload ?? row.detailPayload ?? {};
        const gateName =
          row.gateName
          ?? payload.gate_name
          ?? payload.gateName
          ?? null;
        const gateMet =
          row.gateMet
          ?? payload.gate_met
          ?? payload.gateMet;

        return (
          <article
            className="modifier-manager-card"
            key={
              row.payout_detail_id
              ?? row.payoutDetailId
              ?? `${stageLabel}-${index}`
            }
            style={styles.evidenceRow}
          >
            <div className="modifier-manager-header">
              <h4>{stageLabel}</h4>
              <span className="modifier-manager-badge modifier-manager-badge--readonly">
                {row.component_type ?? row.componentType ?? 'recorded'}
              </span>
            </div>

            <div
              className="modifier-manager-body"
              style={styles.evidenceGrid}
            >
              <div style={styles.item}>
                <span style={styles.label}>Input value</span>
                <span>{formatNumber(inputValue, 6)}</span>
              </div>

              <div style={styles.item}>
                <span style={styles.label}>Resolved rate / factor</span>
                <span>{formatNumber(resolvedRate, 6)}</span>
              </div>

              <div style={styles.item}>
                <span style={styles.label}>Resolved payout</span>
                <span>{formatMoney(resolvedPayout, currency)}</span>
              </div>

              <div style={styles.item}>
                <span style={styles.label}>FX rate applied</span>
                <span>
                  {fxRate === null || fxRate === undefined
                    ? 'Not applicable'
                    : formatNumber(fxRate, 8)}
                </span>
              </div>

              {/*
               * WHY: Gate evidence is rendered only when the engine recorded a gate;
               * the Finance UI never infers a gate from component type.
               */}
              {gateName ? (
                <div style={styles.item}>
                  <span style={styles.label}>Gate</span>
                  <span>
                    {gateName}
                    {' · '}
                    {gateMet === true
                      ? 'Met'
                      : gateMet === false
                        ? 'Not met'
                        : 'State not recorded'}
                  </span>
                </div>
              ) : null}

              {/*
               * WHY: detail_payload is persisted engine evidence. Rendering it preserves
               * fields not known to this presentation layer without inventing semantics.
               */}
              {Object.keys(payload).length > 0 ? (
                <div style={{ ...styles.item, gridColumn: '1 / -1' }}>
                  <span style={styles.label}>Recorded detail payload</span>
                  <pre style={styles.raw}>
                    {JSON.stringify(payload, null, 2)}
                  </pre>
                </div>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}

EvidenceRows.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

function FinanceCompDetail({
  planId,
  fiscalPeriodId,
  positionId,
}) {
  // WHY: Drill level controls navigation only and does not alter economic evidence.
  const [drillLevel, setDrillLevel] = useState(
    positionId ? 'position' : 'plan',
  );

  // WHY: Position selection identifies which persisted engine artifact Finance requests.
  const [selectedPositionId, setSelectedPositionId] = useState(positionId);

  // WHY: Plan detail is server-authoritative plan-period evidence.
  const [planDetail, setPlanDetail] = useState(null);

  // WHY: Position payout contains the recorded engine artifact and is never recomputed.
  const [positionPayout, setPositionPayout] = useState(null);

  // WHY: A single request-state signal prevents Finance from interpreting missing
  // data during retrieval as an unavailable economic result.
  const [loading, setLoading] = useState(false);

  // WHY: Request errors remain distinct from XC-6 result states.
  const [error, setError] = useState('');

  // S1039: Server-side sort + search + cursor pagination. No client-side data manipulation.
  // All rows are server-fetched; sort/search change triggers a fresh fetch from page 1.
  const [sortBy, setSortBy]       = useState('position_name');
  const [sortDir, setSortDir]     = useState('asc');
  const [cursor, setCursor]       = useState(null);
  const [allRows, setAllRows]     = useState([]);
  const [hasMore, setHasMore]     = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm]   = useState('');
  const searchTimerRef = React.useRef(null);

  useEffect(() => {
    // WHY: A parent-driven position context becomes the authoritative initial drill target.
    setSelectedPositionId(positionId);
    setDrillLevel(positionId ? 'position' : 'plan');
  }, [positionId]);

  // S1039: Re-fetch from page 1 when sort or search changes. cursor reset to null.
  useEffect(() => {
    setCursor(null);
    setAllRows([]);
    setHasMore(false);
  }, [sortBy, sortDir, searchTerm, planId, fiscalPeriodId]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadPlanDetail() {
      if (cursor === null) setLoading(true);
      else setLoadingMore(true);
      setError('');

      try {
        const params = new URLSearchParams({
          fiscal_period_id: fiscalPeriodId,
          sort_by:  sortBy,
          sort_dir: sortDir,
          page_size: 100,
        });
        if (cursor) params.set('cursor', cursor);
        if (searchTerm.trim()) params.set('search', searchTerm.trim());

        const response = await fetch(
          `/api/v1/finance/plan/${planId}/detail?${params.toString()}`,
          { method: 'GET', headers: { Accept: 'application/json' }, signal: controller.signal },
        );

        if (!response.ok) throw new Error(`Finance plan detail failed (${response.status}).`);

        const payload = await response.json();
        // First page: replace planDetail meta + reset rows.
        // Subsequent pages: append rows only.
        if (!cursor) {
          setPlanDetail(payload);
          setAllRows(payload.positionRows || []);
        } else {
          setAllRows((prev) => [...prev, ...(payload.positionRows || [])]);
        }
        setHasMore(payload.pagination?.hasMore ?? false);
        setCursor(payload.pagination?.nextCursor ?? null);
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setError(requestError.message || 'Finance plan detail could not be loaded.');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    }

    loadPlanDetail();
    return () => controller.abort();
  }, [planId, fiscalPeriodId, sortBy, sortDir, searchTerm, cursor]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadPositionPayout() {
      // WHY: Position evidence is fetched only when a position has been explicitly
      // selected; null selection remains the plan-summary context.
      if (!selectedPositionId) {
        setPositionPayout(null);
        return;
      }

      setLoading(true);
      setError('');

      try {
        // WHY: payout_summary and payout_detail are persisted engine artifacts and
        // therefore must be read from the Finance evidence endpoint.
        const response = await fetch(
          `/api/v1/finance/position/${encodeURIComponent(
            selectedPositionId,
          )}/payout?fiscal_period_id=${fiscalPeriodId}`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(
            `Finance position payout failed (${response.status}).`,
          );
        }

        const payload = await response.json();
        setPositionPayout(payload);
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setError(
            requestError.message || 'Position payout evidence could not be loaded.',
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadPositionPayout();
    return () => controller.abort();
  }, [selectedPositionId, fiscalPeriodId]);

  const handleViewPosition = (nextPositionId) => {
    // WHY: Drill navigation selects evidence but performs no payout computation.
    setSelectedPositionId(nextPositionId);
    setDrillLevel('position');
  };

  const handleBackToPlan = () => {
    // WHY: Returning to plan summary clears position evidence context only.
    setSelectedPositionId(null);
    setPositionPayout(null);
    setDrillLevel('plan');
  };

  // WHY: Request loading must not be confused with XC-6 financial unavailability.
  if (loading && !planDetail) {
    return (
      <div aria-live="polite" className="modifier-manager-card" role="status">
        <div className="modifier-manager-body">
          <div className="skeleton">Loading Finance compensation detail…</div>
        </div>
      </div>
    );
  }

  // WHY: API failure prevents evidence rendering and must be explicit.
  if (error && !planDetail) {
    return (
      <div className="modifier-manager-error-state" role="alert">
        {error}
      </div>
    );
  }

  // WHY: A successful response without plan detail is distinct from a zero payout.
  if (!planDetail) {
    return (
      <div className="modifier-manager-empty-state" role="status">
        Plan financial detail is not available.
      </div>
    );
  }

  const aggregate = planDetail.periodAggregate || {};
  const aggregateState =
    aggregate.payoutState || 'not_available';

  return (
    <section
      aria-labelledby="finance-comp-detail-title"
      className="modifier-manager-card"
      style={{ fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif' }}
    >
      <div className="modifier-manager-header">
        <div>
          <h2 id="finance-comp-detail-title">
            {planDetail.planName}
          </h2>
          <p>
            {planDetail.planCode} · V{planDetail.planVersion}
          </p>
        </div>
      </div>

      <div className="modifier-manager-body">
        {/*
         * WHY: Effective dates are always visible economic context under DP-1 and
         * are not inferred from plan status.
         */}
        <div style={styles.meta}>
          <div style={styles.item}>
            <span style={styles.label}>Effective from</span>
            <span>{planDetail.effectiveFrom || 'Not available'}</span>
          </div>

          <div style={styles.item}>
            <span style={styles.label}>Effective to</span>
            <span>
              {planDetail.effectiveTo || 'Open-ended / not set'}
            </span>
          </div>

          <div style={styles.item}>
            <span style={styles.label}>Plan version</span>
            <span>V{planDetail.planVersion}</span>
          </div>
        </div>

        {/*
         * WHY: Split-period economic attribution belongs to C-32 and is not
         * reimplemented in this Finance drill-down.
         */}
        {planDetail.isSplitPeriod
        && Array.isArray(planDetail.versionAttribution) ? (
          <VersionAttributionPanel
            periodId={fiscalPeriodId}
            planId={planId}
            versions={planDetail.versionAttribution}
          />
        ) : null}

        {/*
         * WHY: Level 1 renders server-provided plan aggregates and position rows.
         */}
        {drillLevel === 'plan' ? (
          <>
            <div style={styles.metricGrid}>
              <div style={styles.metric}>
                <span style={styles.label}>Projected payout</span>
                <div>
                  <ThreeStateValue
                    formatter={(value) => formatMoney(value)}
                    state={aggregateState}
                    value={aggregate.totalProjectedPayout}
                  />
                </div>
              </div>

              <div style={styles.metric}>
                <span style={styles.label}>Calculated payout</span>
                <div>
                  <ThreeStateValue
                    formatter={(value) => formatMoney(value)}
                    state={aggregateState}
                    value={aggregate.totalCalculatedPayout}
                  />
                </div>
              </div>

              <div style={styles.metric}>
                <span style={styles.label}>Accrual</span>
                <div>
                  <ThreeStateValue
                    formatter={(value) => formatMoney(value)}
                    state={aggregateState}
                    value={aggregate.totalAccrual}
                  />
                </div>
              </div>

              <div style={styles.metric}>
                <span style={styles.label}>Positions</span>
                <div>{aggregate.positionCount ?? 0}</div>
              </div>
            </div>

            {/* S1039: Search box — debounced 400ms, fires server fetch on commit. */}
            <div style={{ marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search by name…"
                value={searchInput}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchInput(val);
                  if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                  searchTimerRef.current = setTimeout(() => setSearchTerm(val), 400);
                }}
                style={{ flex: 1, maxWidth: '20rem', padding: '0.4rem 0.75rem', fontSize: '0.875rem', border: '1px solid rgb(var(--color-border))', borderRadius: '0.5rem', background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => { setSearchInput(''); setSearchTerm(''); }}
                  style={{ fontSize: '0.75rem', color: 'rgb(var(--color-text-muted))', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  Clear
                </button>
              )}
              <span style={{ fontSize: '0.75rem', color: 'rgb(var(--color-text-muted))' }}>
                {allRows.length} shown{hasMore ? '+' : ''}
              </span>
            </div>
            {/* S1039: Sortable column headers — re-fetch from server on click.
                Scrollable container — no client-side data manipulation.
                Load More button — cursor pagination, server-side. */}
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '60vh', border: '1px solid rgb(var(--color-border))', borderRadius: '0.5rem' }}>
              <table className="modifier-manager-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'rgb(var(--color-surface-muted))', zIndex: 1 }}>
                  <tr>
                    {[
                      { key: 'position_name',    label: 'Position name' },
                      { key: null,               label: 'Employee ID' },
                      { key: 'quota_amount',     label: 'Quota' },
                      { key: 'attainment_pct',   label: 'Attainment' },
                      { key: 'calculated_payout', label: 'Calculated payout' },
                      { key: null,               label: 'Actions' },
                    ].map(({ key, label }) => (
                      <th
                        key={label}
                        scope="col"
                        onClick={key ? () => {
                          if (sortBy === key) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
                          else { setSortBy(key); setSortDir('asc'); }
                        } : undefined}
                        style={key ? { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', padding: '0.5rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, color: 'rgb(var(--color-text-muted))' } : { whiteSpace: 'nowrap', padding: '0.5rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, color: 'rgb(var(--color-text-muted))' }}
                      >
                        {label}
                        {key && sortBy === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : key ? ' ⇅' : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allRows.map((row) => (
                    <tr
                      key={row.positionId}
                      onClick={() => handleViewPosition(row.positionId)}
                      style={{ cursor: 'pointer', borderTop: '1px solid rgb(var(--color-border))' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgb(var(--color-surface-muted))'}
                      onMouseLeave={(e) => e.currentTarget.style.background = ''}
                    >
                      <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}>{row.positionName}</td>
                      <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem', color: 'rgb(var(--color-text-muted))' }}>{row.employeeId}</td>
                      <td style={{ ...styles.numeric, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}>
                        <ThreeStateValue formatter={(value) => formatMoney(value)} state={row.payoutState} value={row.quotaAmount} />
                      </td>
                      <td style={{ ...styles.numeric, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}>
                        <ThreeStateValue formatter={formatPercent} state={row.payoutState} value={row.attainmentPct} />
                      </td>
                      <td style={{ ...styles.numeric, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}>
                        <ThreeStateValue formatter={(value) => formatMoney(value)} state={row.payoutState} value={row.calculatedPayout} />
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>
                        <button className="text-button" onClick={(e) => { e.stopPropagation(); handleViewPosition(row.positionId); }} type="button">
                          View detail
                        </button>
                      </td>
                    </tr>
                  ))}
                  {loadingMore && (
                    <tr><td colSpan={6} style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.75rem', color: 'rgb(var(--color-text-muted))' }}>Loading more…</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {hasMore && !loadingMore && (
              <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                <button
                  className="text-button"
                  onClick={() => setCursor(cursor)}
                  type="button"
                  style={{ fontSize: '0.875rem', padding: '0.5rem 1rem', border: '1px solid rgb(var(--color-border))', borderRadius: '0.5rem' }}
                >
                  Load more ({allRows.length} loaded)
                </button>
              </div>
            )}
            {!hasMore && allRows.length > 0 && (
              <p style={{ marginTop: '0.5rem', textAlign: 'center', fontSize: '0.75rem', color: 'rgb(var(--color-text-muted))' }}>
                All {allRows.length} positions loaded.
              </p>
            )}
          </>
        ) : null}

        {/*
         * WHY: Level 2 displays the persisted position payout artifact. It never
         * recomputes any engine stage from input/rate fields.
         */}
        {drillLevel === 'position' ? (
          <div>
            <div style={styles.breadcrumbs}>
              <button
                className="text-button"
                onClick={handleBackToPlan}
                type="button"
              >
                Back to plan
              </button>
              <span aria-hidden="true">/</span>
              <span>Position payout evidence</span>
            </div>

            {loading ? (
              <div aria-live="polite" role="status">
                <div className="skeleton">
                  Loading position payout evidence…
                </div>
              </div>
            ) : null}

            {error ? (
              <div
                className="modifier-manager-inline-error"
                role="alert"
              >
                {error}
              </div>
            ) : null}

            {/*
             * WHY: Rendering begins only when the position evidence endpoint has
             * returned its persisted artifact.
             */}
            {!loading && !error && positionPayout ? (
              <>
                <div style={styles.meta}>
                  <div style={styles.item}>
                    <span style={styles.label}>Position</span>
                    <span>{positionPayout.positionName}</span>
                  </div>

                  <div style={styles.item}>
                    <span style={styles.label}>Employee ID</span>
                    <span>{positionPayout.employeeId}</span>
                  </div>

                  <div style={styles.item}>
                    <span style={styles.label}>Plan version</span>
                    <span>V{positionPayout.planVersion}</span>
                  </div>

                  <div style={styles.item}>
                    <span style={styles.label}>Calculation run ID</span>
                    <span>{positionPayout.calculationRunId}</span>
                  </div>

                  {/*
                   * WHY: resolution cutoff is rendered only when persisted on the
                   * run payload; its absence is expected until m_tc_02.
                   */}
                  {positionPayout.resolutionCutoffAt ? (
                    <div style={styles.item}>
                      <span style={styles.label}>Resolution cutoff</span>
                      <span>{positionPayout.resolutionCutoffAt}</span>
                    </div>
                  ) : null}

                  {/*
                   * WHY: engine build identity is evidence, not an inferred version.
                   */}
                  {positionPayout.engineBuildHash ? (
                    <div style={styles.item}>
                      <span style={styles.label}>Engine build hash</span>
                      <span>{positionPayout.engineBuildHash}</span>
                    </div>
                  ) : null}

                  {/*
                   * WHY: engine release ID is rendered only if the server records it.
                   */}
                  {positionPayout.engineReleaseId ? (
                    <div style={styles.item}>
                      <span style={styles.label}>Engine release ID</span>
                      <span>{positionPayout.engineReleaseId}</span>
                    </div>
                  ) : null}
                </div>

                <div className="modifier-manager-info" role="note">
                  This section renders persisted engine evidence. Values shown below
                  are not recalculated by the Finance UI.
                </div>

                <h3>Engine payout explanation</h3>

                <EvidenceRows
                  rows={
                    Array.isArray(positionPayout.payoutDetail)
                      ? positionPayout.payoutDetail
                      : []
                  }
                />

                <div style={styles.metricGrid}>
                  <div style={styles.metric}>
                    <span style={styles.label}>Settlement contribution</span>
                    <div>
                      <ThreeStateValue
                        formatter={(value) => formatMoney(value)}
                        state={
                          positionPayout.settlementState
                          || 'not_available'
                        }
                        value={positionPayout.settlementContribution}
                      />
                    </div>
                  </div>

                  <div style={styles.metric}>
                    <span style={styles.label}>
                      Recorded total payout
                    </span>
                    <div>
                      {formatMoney(
                        positionPayout.payoutSummary?.total_payout
                        ?? positionPayout.payoutSummary?.totalPayout,
                        positionPayout.payoutSummary?.payout_currency_code
                        ?? positionPayout.payoutSummary?.payoutCurrencyCode
                        ?? 'USD',
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : null}

            {/*
             * WHY: Missing position result is an evidence absence, not a zero payout.
             */}
            {!loading && !error && !positionPayout ? (
              <div className="modifier-manager-empty-state" role="status">
                Position payout evidence is not available.
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

FinanceCompDetail.propTypes = {
  fiscalPeriodId: PropTypes.number.isRequired,
  planId: PropTypes.number.isRequired,
  positionId: PropTypes.string,
};

FinanceCompDetail.defaultProps = {
  positionId: null,
};

/**
 * @example
 * <FinanceCompDetail
 *   planId={418}
 *   fiscalPeriodId={202608}
 *   positionId={null}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 *
 * ✓ DP-1 / INV-1:
 *   Effective dates are always visible at plan level and are rendered directly
 *   from server data.
 *
 * ✓ INV-3:
 *   No workflow status determines plan-version applicability.
 *
 * ✓ INV-7 / DP-6:
 *   EvidenceRows maps every payoutDetail row returned by the server. No fixed
 *   stage list controls which records are rendered and no payout is recalculated.
 *
 * ✓ Split period:
 *   C-32 VersionAttributionPanel is delegated the attribution presentation.
 *
 * ✓ XC-5:
 *   Runtime payout evidence is not described as plan-document validity.
 *
 * ✓ XC-6:
 *   Plan aggregate, position-row, and settlement financial states preserve
 *   available / not available / incomplete-partial distinctions.
 *
 * ✓ Finance evidence:
 *   calculationRunId and optional resolutionCutoffAt, engineReleaseId, and
 *   engineBuildHash are shown as recorded references.
 *
 * ✓ DP-8:
 *   Prohibited Finance approval language is absent.
 */

/**
 * SESSION 6 MIGRATION FLAGS
 *
 * - plan.effective_from / plan.effective_to — pending m_tc_01.
 * - calculation_run.resolution_cutoff_at — pending m_tc_02.
 * - calculation_run.engine_release_id / engine_build_hash — pending m_tc_03.
 * - GET /api/v1/finance/plan/{id}/detail — Session 6 API gap.
 * - GET /api/v1/finance/position/{id}/payout — Session 6 API gap.
 */

/**
 * ACCESSIBILITY NOTES
 *
 * - Plan and position tables use semantic headers.
 * - Drill navigation uses native buttons.
 * - Loading/error/empty evidence states use status or alert semantics.
 * - Engine-stage state and financial availability are written in text.
 * - Financial meaning never depends solely on color.
 */

export default FinanceCompDetail;
